import type { Responsive } from '#types/common'

/**
 * Normalizes a responsive input value into an object with 'sm', 'md', and 'lg' keys.
 *
 * If the input is a scalar value, it is repeated for all breakpoints.
 * If the input is an object, missing breakpoint values are filled by cascading fallbacks.
 *
 * @template T - The type of the responsive values.
 * @param {T | Responsive<T>} obj - The responsive value input, either a scalar or an object with breakpoints.
 * @returns {{ sm: T; md: T; lg: T }} An object with normalized 'sm', 'md', and 'lg' breakpoint values.
 */
export const normalizeResponsive = <T>(obj: T | Responsive<T>) => {
  // If scalar, repeat across all breakpoints
  if (!isObject(obj))
    return { sm: obj as T, md: obj as T, lg: obj as T }

  return {
    sm: obj.sm,
    md: obj.md ?? obj.sm,
    lg: obj.lg ?? obj.md ?? obj.sm
  }
}
